import { memo, type FC } from "react";
import { StatusCardContainer } from "@src/components/CameraDetails";
import { StatusRow, SimpleIcon, NavigationRowRightContent } from "./StatusRow";
import { NavPressable } from "@src/components/NavPressable";
import { ActivityLastDayHeatmap } from "@src/features/clips";
import type { Href } from "expo-router";

interface ScheduleMenuCardProps {
  scheduleStatus: string;
  /** Undefined while the count is loading. */
  alertsCount: number | undefined;
  siteId: string;
  cameraKey: string;
  timezone: string;
  onAlarmSettingsPress: () => void;
  onRecordingsPress: () => void;
  onTimelapsePress?: () => void;
  timelapseSubtitle?: string;
  timelapseDisabled?: boolean;
  alarmSettingsHref?: Href;
  recordingsHref?: Href;
  onManageAccessPress?: () => void;
  isManageAccessEnabled?: boolean;
  onSceneComparePress?: () => void;
  onTestSignalsPress?: () => void;
  onNotificationSettingsPress?: () => void;
}

export const ScheduleMenuCard: FC<ScheduleMenuCardProps> = memo(
  ({
    scheduleStatus,
    alertsCount,
    siteId,
    cameraKey,
    timezone,
    onAlarmSettingsPress,
    onRecordingsPress,
    onTimelapsePress,
    timelapseSubtitle = "Past 7 days",
    timelapseDisabled = false,
    alarmSettingsHref,
    recordingsHref,
    onManageAccessPress,
    isManageAccessEnabled = false,
    onSceneComparePress,
    onTestSignalsPress,
    onNotificationSettingsPress,
  }) => {
    // While loading, the placeholder text only sizes the skeleton; it is never shown.
    const clipsText =
      alertsCount === undefined ? "00 clips past 24 hrs"
      : alertsCount === 1 ? "1 clip past 24 hrs"
      : `${alertsCount} clips past 24 hrs`;
    const recordingsRightContent = (
      <NavigationRowRightContent value={clipsText} isLoading={alertsCount === undefined} />
    );

    return (
      <StatusCardContainer title="Schedule">
        {/* Alarm Settings Row */}
        {alarmSettingsHref ?
          <NavPressable href={alarmSettingsHref}>
            <StatusRow
              icon={<SimpleIcon name="calendarOutline" />}
              title="Alarm settings"
              rightContent={<NavigationRowRightContent value={scheduleStatus} />}
            />
          </NavPressable>
        : <StatusRow
            icon={<SimpleIcon name="calendarOutline" />}
            title="Alarm settings"
            rightContent={<NavigationRowRightContent value={scheduleStatus} />}
            onPress={onAlarmSettingsPress}
          />
        }

        {/* Recordings Row with Heatmap */}
        {recordingsHref ?
          <NavPressable href={recordingsHref}>
            <StatusRow
              icon={<SimpleIcon name="activity" />}
              title="Recordings"
              rightContent={recordingsRightContent}
              bottomContent={<ActivityLastDayHeatmap siteId={siteId} cameraKey={cameraKey} timezone={timezone} />}
            />
          </NavPressable>
        : <StatusRow
            icon={<SimpleIcon name="activity" />}
            title="Recordings"
            rightContent={recordingsRightContent}
            bottomContent={<ActivityLastDayHeatmap siteId={siteId} cameraKey={cameraKey} timezone={timezone} />}
            onPress={onRecordingsPress}
          />
        }

        {/* Notification Settings Row */}
        {onNotificationSettingsPress && (
          <StatusRow
            icon={<SimpleIcon name="bellOutline" />}
            title="Notification settings"
            rightContent={<NavigationRowRightContent value="" />}
            onPress={onNotificationSettingsPress}
          />
        )}

        {/* Users & Permissions Row — gated on USERS_PERMISSIONS_OPTION (mirrors native camera page) */}
        {isManageAccessEnabled && onManageAccessPress && (
          <StatusRow
            icon={<SimpleIcon name="users" />}
            title="Users & permissions"
            rightContent={<NavigationRowRightContent value="" />}
            onPress={onManageAccessPress}
          />
        )}

        {/* Timelapse row always uses `onPress` so analytics fires before
            request/download work begins. */}
        {onTimelapsePress ?
          <StatusRow
            icon={<SimpleIcon name="videoOutline" />}
            title="Timelapse"
            rightContent={<NavigationRowRightContent value={timelapseSubtitle} showChevron={false} />}
            onPress={onTimelapsePress}
            disabled={timelapseDisabled}
          />
        : null}

        {onSceneComparePress && (
          <StatusRow
            icon={<SimpleIcon name="swapVertical" />}
            title="Scene Compare"
            rightContent={<NavigationRowRightContent value="Support" />}
            onPress={onSceneComparePress}
          />
        )}

        {onTestSignalsPress && (
          <StatusRow
            icon={<SimpleIcon name="send" />}
            title="Test signals"
            rightContent={<NavigationRowRightContent value="Support" />}
            onPress={onTestSignalsPress}
          />
        )}
      </StatusCardContainer>
    );
  },
);
